import { Avatar, Tag } from '@ads/components-react';
import { CameraIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePersonPhoto } from '../../../hooks/usePersonPhoto';
import { useUploadPhoto } from '../../../hooks/useUploadPhoto';
import type { Person } from '../../../types/person';
import { UploadPhotoModal } from './UploadPhotoModal';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface PersonFormHeaderProps {
  readonly person: Person | undefined;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(person: Person | undefined): string {
  if (!person) return '?';
  const first = person.firstName?.[0] ?? '';
  const last = person.lastName?.[0] ?? '';
  return `${first}${last}`.toUpperCase() || '?';
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Cabeçalho contextual do formulário de pessoa. Exibe avatar com iniciais,
 * nome completo, CPF e o badge de status (ativo/inativo) em modo edição.
 * Em modo criação, mostra placeholder neutro.
 * Em modo edição, o avatar é clicável e abre o modal de upload de foto.
 *
 * @param person - Pessoa em edição. `undefined` em modo criação.
 */
export function PersonFormHeader({ person }: PersonFormHeaderProps) {
  const { t } = useTranslation('personManagement');

  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const fullName =
    person?.fullName ?? [person?.firstName, person?.lastName].filter(Boolean).join(' ');
  const displayName = fullName?.trim() || t('personForm.header.fallback');
  const cpfDisplay = person?.cpf || t('personForm.header.noCpf');
  const initials = getInitials(person);

  const photoUrl = usePersonPhoto(person?.id);
  const uploadPhoto = useUploadPhoto(person?.id ?? 0);

  // Fecha automaticamente após sucesso, dando tempo ao usuário ver o feedback
  useEffect(() => {
    if (!uploadPhoto.isSuccess) return;
    const timer = setTimeout(() => {
      setIsPhotoModalOpen(false);
      uploadPhoto.reset();
    }, 1500);
    return () => clearTimeout(timer);
  }, [uploadPhoto.isSuccess, uploadPhoto.reset]);

  const handleModalClose = () => {
    setIsPhotoModalOpen(false);
    uploadPhoto.reset();
  };

  const handlePhotoSubmit = (file: File) => {
    if (!person) return;
    uploadPhoto.mutate({ id: person.id, data: { file } });
  };

  return (
    <div className="flex items-center gap-xxs rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
      <button
        type="button"
        className="group relative cursor-pointer"
        onClick={() => setIsPhotoModalOpen(true)}
        aria-label={t('personForm.header.changePhoto')}
      >
        <Avatar size="lg" src={photoUrl} fallback={initials} alt={displayName} />
        <div className="absolute inset-none flex items-center justify-center rounded-circular bg-ctx-layout-body opacity-transparent transition-opacity group-hover:bg-base-gray-200 group-hover:opacity-opaque">
          <CameraIcon className="h-xxs w-xxs text-base-neutral-black" />
        </div>
      </button>

      <div className="flex min-w-none flex-1 flex-col gap-quark">
        <div className="flex flex-wrap items-center gap-xxs">
          <h2 className="ts-heading-md text-ctx-content-title">{displayName}</h2>

          <Tag
            size="sm"
            status={person?.active ? 'positive' : 'default'}
            label={
              person?.active
                ? t('personForm.header.activeBadge')
                : t('personForm.header.inactiveBadge')
            }
            custom={{ text: 'text-nowrap' }}
          />
        </div>

        <div className="flex flex-wrap items-center gap-xs ts-paragraph-xs text-ctx-content-base">
          <span>{cpfDisplay}</span>

          <span className="text-ctx-content-disabled" aria-hidden="true">
            ·
          </span>
          <span className="text-ctx-content-title">
            {t('personForm.header.codeLabel')} {person?.code}
          </span>
        </div>
      </div>

      <UploadPhotoModal
        open={isPhotoModalOpen}
        isLoading={uploadPhoto.isPending}
        isError={uploadPhoto.isError}
        isSuccess={uploadPhoto.isSuccess}
        onClose={handleModalClose}
        onSubmit={handlePhotoSubmit}
      />
    </div>
  );
}
